import React, { useCallback, useState } from 'react';
import { createSyntheticEvent } from '../../utils/createSyntheticEvent';

/**
 * Keeps a history of image source requests so we don't try to load a source multiple times if we
 * already know it's bad.
 *
 * Keys are the image source URLs and values are booleans representing:
 * - `undefined` means we haven't tried to load the source yet.
 * - `true` means we've successfully loaded the source, so it should be in the browser cache.
 * - `false` means we've tried to load the source and it failed.
 * */
const srcHealthCache = new Map<string, boolean>();

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** A fallback to be displayed if loading the src image fails. */
  fallback?: string | React.ReactElement | false;
}

/**
 * An image component that supports a fallback image if the src image fails to load.
 */
const Image = React.forwardRef<HTMLImageElement | null, ImageProps>(
  (
    {
      decoding = 'async',
      fallback,
      loading = 'lazy',
      onError,
      onLoad,
      sizes,
      src,
      srcSet,
      ...rest
    },
    forwardedRef
  ) => {
    const srcIsBad = !src || (typeof src === 'string' && srcHealthCache.get(src) === false);
    const fallbackSrcIsBad = typeof fallback !== 'string' || srcHealthCache.get(fallback) === false;

    const [showFallbackElement, setShowFallback] = useState(srcIsBad && fallbackSrcIsBad);

    const handleLoad = useCallback(
      (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.target as HTMLImageElement;

        if (e.type === 'error') {
          srcHealthCache.set(img.currentSrc, false);
          if (typeof fallback === 'string' && srcHealthCache.get(fallback) !== false) {
            img.src = fallback;
          } else {
            setShowFallback(true);
          }
        } else {
          srcHealthCache.set(img.currentSrc, true);
        }
      },
      [fallback]
    );

    // Set the forward ref and ensures the load event is fired even if the image is already loaded
    const ref = useCallback(
      (img: HTMLImageElement | null) => {
        if (forwardedRef) {
          if (typeof forwardedRef === 'function') {
            forwardedRef(img);
          } else if (typeof forwardedRef === 'object') {
            forwardedRef.current = img;
          }
        }

        if (img?.complete) {
          handleLoad(createSyntheticEvent('load', img));
        }
      },
      [forwardedRef, handleLoad]
    );

    if (showFallbackElement) {
      return React.isValidElement(fallback) ? fallback : null;
    }

    const srcOrFallback =
      srcIsBad && typeof fallback === 'string' && !fallbackSrcIsBad ? fallback : src;

    return (
      <img
        {...rest}
        decoding={decoding}
        loading={loading}
        onLoad={e => {
          handleLoad(e);
          onLoad?.(e);
        }}
        onError={e => {
          handleLoad(e);
          onError?.(e);
        }}
        sizes={sizes}
        srcSet={srcSet}
        // Keep `src` after `sizes` and `srcSet` to prevent unnecessary requests in Safari
        src={srcOrFallback}
        ref={ref}
      />
    );
  }
);

Image.displayName = 'Image';
export default Image;
